import type { EndpointStatus } from '@/hooks/use-endpoint-status';
import { AI_ENABLED } from '@/lib/build-flags';
import type { TransportMode } from '@/types';

export interface ResolveTransportModeInput {
	/** Mode requested via the URL (`?mode=`) — picker choice or embed hint. */
	requested?: TransportMode;
	/** Semicolon-delimited Features__c baked into the partner page / embed tag. */
	urlFeatures?: string;
	/** Result of the endpoint-status probe (may be loading after timeout). */
	status: EndpointStatus;
	/** Whether this deployment has Amazon Connect transport config at all. */
	hasConnectConfig: boolean;
	/**
	 * Whether the AI surface exists in this *build*. Defaults to the compile-time
	 * flag; passed explicitly only by tests, which need to exercise both.
	 *
	 * Distinct from entitlement: Salesforce says what a district bought,
	 * this says what the bundle on the page can actually do. The Connect-only
	 * element ships without the AppSync transport, so a district whose
	 * Features__c says Chatbot still cannot be routed there — it would resolve a
	 * mode with nothing behind it.
	 */
	aiAvailable?: boolean;
}

export type FeatureChatMode = 'ai' | 'connect' | 'both';

/** Resolve the chat surfaces selected in Salesforce Features__c. */
export function chatModeFromFeatures(urlFeatures?: string): FeatureChatMode {
	const features = (urlFeatures ?? '')
		.split(';')
		.map((feature) => feature.trim())
		.filter(Boolean);
	const hasChatbot = features.includes('Chatbot');
	const hasHelpline = features.includes('Helpline');
	if (hasChatbot && hasHelpline) return 'both';
	if (hasChatbot) return 'ai';
	return 'connect';
}

/**
 * Decide which transport a student lands on. Precedence:
 *
 * 1. The endpoint-status probe is authoritative — flipping Features__c in
 *    Salesforce retargets every embed without district admins touching their
 *    CMS pages. `connect` forces classic-chat; `ai` forces reachout-ai;
 *    `both` honors the request (AI is the default surface).
 * 2. While the probe hasn't answered (loading/timeout) the embed's baked
 *    `?features=` hint stands in for it. A single-surface district pins the
 *    mode here too: `?mode=` is a request, not an entitlement, so hand-editing
 *    it can't open the surface Salesforce disabled. Only a `both` district
 *    lets the request through.
 * 3. With no feature signal at all, an explicit request stands (dev previews,
 *    legacy embeds that bake no features); otherwise prefer Connect when it's
 *    configured — a human counselor is the safe default for a support line.
 */
export function resolveTransportMode({
	requested,
	urlFeatures,
	status,
	hasConnectConfig,
	aiAvailable = AI_ENABLED,
}: ResolveTransportModeInput): TransportMode {
	// Before any entitlement is consulted: a build with no AI has exactly one
	// surface, and routing to the other one would strand the student on a
	// transport that throws.
	if (!aiAvailable) return 'classic-chat';

	if (status.state === 'enabled' && status.chatMode) {
		switch (status.chatMode) {
			case 'connect':
				return 'classic-chat';
			case 'ai':
				return 'reachout-ai';
			case 'both':
				// AI is the default surface, but an explicit counselor choice stands.
				return requested ?? 'reachout-ai';
		}
	}

	if (urlFeatures !== undefined) {
		switch (chatModeFromFeatures(urlFeatures)) {
			case 'ai':
				return 'reachout-ai';
			case 'connect':
				return 'classic-chat';
			case 'both':
				return requested ?? 'classic-chat';
		}
	}

	if (requested) return requested;

	return hasConnectConfig ? 'classic-chat' : 'reachout-ai';
}

export interface ChatSurfacesInput {
	/** Semicolon-delimited Features__c baked into the partner page / embed tag. */
	urlFeatures?: string;
	/** Result of the endpoint-status probe. */
	status: EndpointStatus;
	/** Whether the AI surface exists in this build. See ResolveTransportModeInput. */
	aiAvailable?: boolean;
}

/**
 * Which surfaces this district is entitled to, or `undefined` when neither
 * signal has answered. Same precedence as `resolveTransportMode`: the probe is
 * authoritative, the baked `?features=` hint stands in until it answers.
 *
 * `undefined` is deliberately distinct from a value: callers decide which way
 * to fail. Gates that *reveal* a surface must treat it as "no" (never offer a
 * mode we can't confirm); gates that *withdraw* one must treat it as "yes"
 * (never retract a safety affordance on a guess).
 */
export function chatSurfaces({
	urlFeatures,
	status,
	aiAvailable = AI_ENABLED,
}: ChatSurfacesInput): FeatureChatMode | undefined {
	const entitled =
		status.state === 'enabled' && status.chatMode
			? status.chatMode
			: urlFeatures !== undefined
				? chatModeFromFeatures(urlFeatures)
				: undefined;
	if (entitled === undefined) return undefined;
	// A Connect-only build collapses every entitlement to the one surface it
	// has, so the picker never renders and the header offers no way back to it.
	return aiAvailable ? entitled : 'connect';
}

/**
 * Whether the student is entitled to *both* surfaces — the only case where the
 * chat-type picker may render, and where the chat header may offer a way back
 * to it. Unknown counts as no.
 */
export function bothChatModesAvailable(input: ChatSurfacesInput): boolean {
	return chatSurfaces(input) === 'both';
}

/**
 * Whether the district is *known* to have Helpline switched off. Gates the
 * deliberate "talk to a counselor" affordances in AI mode — not the safety
 * ladder, which still hands off to Connect when the AI path breaks.
 *
 * Unknown counts as "Helpline is available": withdrawing the counselor route
 * from a district that actually has it is the worse failure on a support line.
 */
export function isAiOnlyDistrict(input: ChatSurfacesInput): boolean {
	return chatSurfaces(input) === 'ai';
}
